import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormLabelProps,
} from '@chakra-ui/react';
import { SelectOption } from '@diamond/shared/types';
import { AsyncSelect, ChakraStylesConfig } from 'chakra-react-select';
import { Controller, useFormContext } from 'react-hook-form';

export interface SelectAutocompleteProps {
  name: string;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  id?: string;
  labelProps?: FormLabelProps;
  options?: SelectOption[];
  loadOptions?: (query: string) => Promise<SelectOption[]>;
  chakraStyles?: ChakraStylesConfig<SelectOption, false>;
  onChange?: (value: string | null, option?: SelectOption) => void;
  emptyMessage?: React.ReactNode;
  debounce?: number;
  getOptions?: (query: string) => Promise<SelectOption[]>;
  isDisabled?: boolean;
}

const defaultChakraStyles: ChakraStylesConfig<SelectOption, false> = {
  container: (base) => ({ ...base, minWidth: 160 }),
  menu: (base) => ({ ...base, zIndex: 98 }),
  indicatorSeparator: () => ({ display: 'none' }),
  valueContainer: (base) => ({ ...base, flexWrap: 'nowrap' }),
  control: (base, state) => ({
    ...base,
    minHeight: 10,
    borderRadius: 'var(--chakra-radii-md)',
    ...(state.isDisabled && {
      backgroundColor: 'var(--chakra-colors-gray-200)',
      borderColor: 'var(--chakra-colors-gray-300)',
      opacity: 1,
      cursor: 'not-allowed',
    }),
  }),
  singleValue: (base, state) => ({
    ...base,
    ...(state.isDisabled && {
      color: 'var(--chakra-colors-gray-600)',
    }),
  }),
  placeholder: (base, state) => ({
    ...base,
    ...(state.isDisabled && {
      color: 'var(--chakra-colors-gray-500)',
    }),
  }),
  input: (base, state) => ({
    ...base,
    ...(state.isDisabled && {
      color: 'var(--chakra-colors-gray-600)',
    }),
  }),
};

export function SelectAutocomplete({
  name,
  label,
  options,
  placeholder,
  defaultValue,
  onChange,
  labelProps,
  loadOptions,
  getOptions,
  emptyMessage,
  debounce = 300,
  id,
  chakraStyles,
  isDisabled = false,
  ...props
}: SelectAutocompleteProps) {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const isInvalid = errors ? !!errors[name] : false;
  const errorMessage = errors ? errors[name]?.message : '';

  // Use loadOptions if provided, otherwise fall back to static options
  const asyncLoadOptions =
    loadOptions || getOptions || (() => Promise.resolve(options || []));

  return (
    <FormControl id={id} isInvalid={isInvalid}>
      {label && (
        <FormLabel aria-label={label} htmlFor={id} {...labelProps}>
          {label}
        </FormLabel>
      )}
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue ?? ''}
        render={({ field }) => {
          // Find the option object for the current field value
          const selectedOption =
            options?.find((opt) => opt.value === field.value) || null;

          const defaultOptionsProp =
            options && Array.isArray(options) && options.length > 0
              ? options
              : true;

          return (
            <AsyncSelect
              {...props}
              chakraStyles={chakraStyles || defaultChakraStyles}
              loadOptions={asyncLoadOptions}
              defaultOptions={defaultOptionsProp}
              // ensure we trigger a load when the user opens the menu
              onMenuOpen={() => {
                // call to prime options; ignore result since AsyncSelect will also call loadOptions
                try {
                  asyncLoadOptions('');
                } catch (e) {
                  /* ignore */
                }
              }}
              value={selectedOption}
              placeholder={placeholder}
              noOptionsMessage={() => emptyMessage || 'No options found'}
              onChange={(selected) => {
                const value = selected?.value || null;
                field.onChange(value);
                if (onChange) onChange(value, selected || undefined);
              }}
              isClearable
              isSearchable
              cacheOptions
              defaultMenuIsOpen={false}
              isDisabled={isDisabled}
            />
          );
        }}
      />
      <FormErrorMessage textColor="red">
        {errorMessage as string}
      </FormErrorMessage>
    </FormControl>
  );
}
